import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Fade,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Description as DescriptionIcon,
  Gavel as GavelIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { publicService } from '../../services/api';

const Terms = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [settings, setSettings] = useState({
    business: {
      name: '',
      email: '',
      phone: ''
    }
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await publicService.getSettings();
        const data = response.data.data;
        setSettings({
          business: {
            name: data.business_name || 'ASO',
            email: data.business_email || '',
            phone: data.business_phone || ''
          }
        });
      } catch (error) {
        console.error('Ayarlar yüklenirken hata oluştu:', error);
      }
    };

    fetchSettings();
  }, []);

  return (
    <Box sx={{ py: { xs: 4, md: 8 } }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Box>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: 'primary.main',
                  mb: 2,
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Kullanım Koşulları
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto' }}
              >
                Hizmetlerimizi kullanırken uyulması gereken kurallar ve koşullar
              </Typography>
            </Box>

            <Paper
              elevation={3}
              sx={{
                p: { xs: 3, md: 6 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, #fafdff 0%, #e3f0fc 100%)'
              }}
            >
              {/* Son Güncelleme */}
              <Box sx={{ mb: 4, p: 3, bgcolor: 'primary.50', borderRadius: 2 }}>
                <Typography variant="h6" color="primary.main" sx={{ fontWeight: 600, mb: 1 }}>
                Son Güncelleme: 08.08.2025
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Bu kullanım koşulları, {settings.business.name} tarafından yönetilmektedir.
                </Typography>
              </Box>

              {/* Bölümler */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  1. Genel Hükümler
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Bu kullanım koşulları, {settings.business.name} tarafından sunulan otomotiv servis hizmetlerinin kullanımını düzenler. 
                  Hizmetlerimizi kullanarak bu koşulları kabul etmiş sayılırsınız.
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Kabul"
                      secondary="Hizmetlerimizi kullanarak bu koşulları kabul etmiş sayılırsınız"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <GavelIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Yasal Uygunluk"
                      secondary="Tüm işlemler Türkiye Cumhuriyeti yasalarına uygun olarak yapılır"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <AssignmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  2. Hizmet Kapsamı
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Sunduğumuz hizmetler aşağıdaki kategorileri kapsar:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Periyodik Bakım"
                      secondary="Motor yağı değişimi, filtre değişimi, genel kontrol"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Arıza Teşhisi ve Onarım"
                      secondary="Motor, şanzıman, elektrik sistemi arızaları"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Yedek Parça Değişimi"
                      secondary="Fren sistemi, süspansiyon, aksesuar değişimi"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Araç Muayenesi"
                      secondary="Emisyon testi, güvenlik kontrolü"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  3. Ödeme ve Fiyatlandırma
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Hizmet fiyatları ve ödeme koşulları:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Fiyatlandırma"
                      secondary="Fiyatlarımız şeffaf ve önceden belirlenmiştir"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Ödeme Yöntemleri"
                      secondary="Nakit, kredi kartı, banka kartı ile ödeme kabul edilir"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Garanti"
                      secondary="Yapılan işler için uygun garanti süresi verilir"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <CancelIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  4. Randevu ve İptal Koşulları
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Randevu alma ve iptal etme koşulları:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Randevu Alma"
                      secondary="Online veya telefon ile randevu alabilirsiniz"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="İptal Süresi"
                      secondary="Randevu saatinden en az 2 saat önce iptal edilmelidir"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Geç Kalma"
                      secondary="15 dakikadan fazla geç kalınması durumunda randevu iptal edilebilir"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  5. Sorumluluk Sınırları
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Hizmet sürecinde oluşabilecek durumlar:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <ErrorIcon color="error" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Beklenmeyen Arızalar"
                      secondary="Servis sırasında tespit edilen ek arızalar için ayrıca bilgilendirme yapılır"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <WarningIcon color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Yedek Parça Temini"
                      secondary="Bazı yedek parçaların temini zaman alabilir"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <CheckCircleIcon color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Kalite Garantisi"
                      secondary="Yapılan işler için kalite garantisi verilir"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  6. Müşteri Hakları ve Yükümlülükleri
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Müşteri olarak haklarınız ve yükümlülükleriniz:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="Doğru Bilgi Verme"
                      secondary="Araç bilgilerini doğru ve eksiksiz verme yükümlülüğü"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Araç Hazırlığı"
                      secondary="Servis için araç anahtarlarını ve gerekli belgeleri getirme"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="İletişim"
                      secondary="Randevu değişikliklerinde önceden bilgilendirme"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Ödeme"
                      secondary="Hizmet sonrası ödeme yapma yükümlülüğü"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  7. Gizlilik ve Veri Güvenliği
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Kişisel verilerinizin korunması:
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText
                      primary="KVKK Uyumluluğu"
                      secondary="Kişisel veriler KVKK'ya uygun olarak işlenir"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Veri Güvenliği"
                      secondary="Tüm veriler güvenli sistemlerde saklanır"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="Üçüncü Taraf Paylaşımı"
                      secondary="Kişisel veriler izinsiz üçüncü taraflarla paylaşılmaz"
                    />
                  </ListItem>
                </List>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  8. Değişiklikler ve Güncellemeler
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Bu kullanım koşulları zaman zaman güncellenebilir. Önemli değişiklikler önceden duyurulur.
                </Typography>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.dark' }}>
                  9. İletişim
                </Typography>
                <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.8 }}>
                  Kullanım koşulları hakkında sorularınız için bizimle iletişime geçebilirsiniz:
                </Typography>
                <Box sx={{ p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>E-posta:</strong> {settings.business.email || 'info@aso.com'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Telefon:</strong> {settings.business.phone || '+90 xxx xxx xx xx'}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Adres:</strong> {settings.business.name} - Türkiye
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default Terms; 